// THE EVENFOLD — the real game's rooms (greybox; brilliant art comes after the design is
// locked). Authored from research/writers/canon + the DeepSeek/Kimi Room-1 designs. Loaded
// via ?world=evenfold; the oracle sandbox stays the default world.
//
// MI1-style framing: rooms are ~one screen TALL (h=270 — a shallow floor band at the bottom,
// a tall backdrop above) and scroll horizontally when wider than the view. The view fits this
// height to the window (renderer.applyViewport), so characters read large (≈1/5 screen, like
// Guybrush) and the room fills the screen with no letterbox. Placeholder art = colored shapes.
import { loadRoom, type Room } from "../core/schema";
import type { QuestSpec } from "../engine/quest";

// ---- Room 1: "The Even Hall" — Act I, the Banking. SINGLE-SCREEN (480×270), geometry adapted to
// the painted art: a warm longhouse interior, the great communal HEARTH at centre, the way out (a
// door) at the LEFT, Coalmother Vess tending the coals and the Eldest near the door. Brand wakes
// carrying his small flame; tending the great hearth one last time (kindling it) is the Act-I beat
// (wins `hall_opened`); then he may leave by the door for the Doorstone. ----
const evenHall = {
  id: "even_hall",
  name: "The Even Hall",
  size: { w: 480, h: 270 },
  background: { color: 0x1a120a },
  bgImage: "art/even_hall.png", // gorgeous native pixel art (Retro-Diffusion); geometry aligned to it
  spawn: { x: 210, y: 250 },
  actorRadius: 8,
  interactRadius: 64,
  state: {},
  // walkable floor = the stone hall floor across the foreground (Brand walks in front of the hearth)
  walkmesh: [
    [ { x: 42, y: 224 }, { x: 452, y: 224 }, { x: 452, y: 268 }, { x: 38, y: 268 } ],
  ],
  hotspots: [
    // the great communal hearth at the heart of the hall — tend it one last time (your flame grows,
    // and you have done the keeping); the Act-I "Banking" beat and the quest win.
    { id: "great_hearth", verb: "kindle", label: "the great hearth", interactAt: { x: 238, y: 244 },
      line: "The great hearth at the heart of the hall, banked even and low like all the rest — the fire the whole Evenfold gathers to keep. I kneel and feed it from my own small flame, and for one breath it leaps up tall and glad, throwing every shadow back. I have kept the fire. Now I must carry it.",
      effect: { flame: 1, setGlobal: { hall_opened: true } },
      polygon: [ { x: 206, y: 184 }, { x: 276, y: 184 }, { x: 276, y: 226 }, { x: 206, y: 226 } ] },
    // a wall-banner — grace / lore (the keeping-song read as comfort, or as a cage)
    { id: "banner", verb: "examine", label: "a woven banner", interactAt: { x: 408, y: 246 },
      line: "A woven wool banner, faded madder-red, the old keeping-song picked out in its threads: 'Even, even, little light — bank you low.' Generations have read it as comfort. Tonight I read it as a cage.",
      polygon: [ { x: 392, y: 110 }, { x: 434, y: 110 }, { x: 434, y: 182 }, { x: 392, y: 182 } ] },
  ],
  // living-scene effects: the great hearth blazes & throws embers, a warm glow breathes over it,
  // the two wall-sconces glow, and firelit dust drifts through the hall air. (Placed to the art.)
  fx: [
    { id: "hearth_glow", kind: "glow", layer: "back", x: 188, y: 130, w: 100, h: 96, color: 0xffae46, intensity: 1.1, seed: 1 },
    { id: "hearth_flames", kind: "flames", layer: "back", x: 194, y: 166, w: 72, h: 56, intensity: 1.05, seed: 2 },
    { id: "hearth_embers", kind: "embers", layer: "front", x: 216, y: 150, w: 44, h: 56, count: 14, intensity: 1, seed: 3 },
    { id: "sconce_l", kind: "glow", layer: "back", x: 92, y: 104, w: 30, h: 34, color: 0xff9e42, intensity: 0.7, seed: 4 },
    { id: "sconce_r", kind: "glow", layer: "back", x: 286, y: 104, w: 30, h: 34, color: 0xff9e42, intensity: 0.7, seed: 5 },
    { id: "hall_motes", kind: "motes", layer: "front", x: 44, y: 58, w: 392, h: 120, color: 0xffe2a8, count: 24, intensity: 1, seed: 6 },
  ],
  npcs: [
    { id: "vess", x: 312, y: 240, radius: 9, color: 0x6e8b6e, label: "Coalmother Vess",
      dialog: [
        { text: "Brand, child — come and sit by the hearth a while. The coals are evenest tonight, and the safest. Why do you carry your flame so high?", mood: "warm" },
        { text: "You have the look your mother wore, the winter before the Grey took her warmth. Bank it down, love. Stay where it is warm.", mood: "warm" },
      ] },
    { id: "eldest", x: 172, y: 238, radius: 9, color: 0x8b7e6e, label: "the Eldest",
      dialog: [
        { text: "So. You have found the door that faces in, and seen that it opens out.", mood: "sad" },
        { text: "I will not stop you. I am too old, and I have wondered too long what lies past it. Carry it well, Brand. Carry it far.", mood: "sad" },
      ] },
  ],
  exits: [
    // the way out — the door at the left of the hall. Opens once you have tended the great hearth.
    { id: "to_open", target: "doorstone", spawn: { x: 165, y: 232 }, transition: "fade",
      gatedBy: { key: "hall_opened", scope: "global" },
      polygon: [ { x: 80, y: 222 }, { x: 132, y: 222 }, { x: 132, y: 268 }, { x: 74, y: 268 } ] },
  ],
};

// ---- Room 2: "The Doorstone" — the Act I→II threshold. SINGLE-SCREEN (480×270), geometry adapted
// to the painted art (art-first pipeline): the longhouse + doorway-fire on the LEFT (Brand exits the
// hall here), lanterns flanking the door, the cobbled yard as the walkable floor, the road curving
// RIGHT into the vale = the east exit. Warm the watch-fire, then the two lanterns (the flame grows),
// until it is bright enough to take the Dark Road. Pure flame-spine gate. ----
const doorstone = {
  id: "doorstone",
  name: "The Doorstone",
  size: { w: 480, h: 270 },
  background: { color: 0x161a20 },
  bgImage: "art/doorstone.png", // gorgeous native pixel art (Retro-Diffusion); geometry aligned to it
  spawn: { x: 165, y: 232 }, // on the cobbled yard, just below the longhouse steps
  actorRadius: 8,
  interactRadius: 64,
  state: { watchfire_warmed: false, lantern_left_lit: false, lantern_right_lit: false, doorstone_read: false },
  // walkable floor = the cobbled yard/path across the bottom of the painting
  walkmesh: [
    [ { x: 50, y: 213 }, { x: 478, y: 213 }, { x: 478, y: 264 }, { x: 46, y: 264 } ],
  ],
  hotspots: [
    // the watch-fire blazing in the open doorway (left) — warm it; your flame grows
    { id: "watch_fire", verb: "kindle", label: "the last watch-fire", interactAt: { x: 132, y: 224 },
      line: "The last watch-fire of the fold, blazing in the open doorway. Past it there is no more keeping. I feed it from my own flame, and it leaps up tall and uneven and glad — and throws my shadow long across the cobbles, pointing the way out.",
      gatedBy: { minFlame: 1 }, effect: { flame: 1, set: { watchfire_warmed: true } },
      polygon: [ { x: 112, y: 150 }, { x: 150, y: 150 }, { x: 150, y: 206 }, { x: 112, y: 206 } ] },
    // the worn doorstone / steps — a grace note (the keeping-song graven, the Kindler's rhyme beneath)
    { id: "doorstone_mark", verb: "examine", label: "the worn Doorstone", interactAt: { x: 160, y: 226 },
      line: "The threshold stone at the foot of the steps, worn smooth by every foot that ever left and came back even. Graven across it: 'Even, even, little light — bank you low.' And scratched faint beneath, by some hand long gone: 'but I burned where no flame had burned before.'",
      effect: { set: { doorstone_read: true } },
      polygon: [ { x: 134, y: 198 }, { x: 188, y: 198 }, { x: 188, y: 214 }, { x: 134, y: 214 } ] },
    // the two porch-lanterns flanking the door — kindle once the watch-fire is warmed; each grows the flame
    { id: "lantern_left", verb: "kindle", label: "a frost-choked porch-lantern", interactAt: { x: 96, y: 222 },
      line: "A porch-lantern choked with frost, dark for want of tending. I breathe my flame into it; the rime cracks and weeps, and a warm pool of light spreads across the porch.",
      gatedBy: { key: "watchfire_warmed", scope: "room" }, effect: { flame: 1, set: { lantern_left_lit: true } },
      polygon: [ { x: 72, y: 106 }, { x: 94, y: 106 }, { x: 94, y: 152 }, { x: 72, y: 152 } ] },
    { id: "lantern_right", verb: "kindle", label: "a frost-choked porch-lantern", interactAt: { x: 188, y: 224 },
      line: "The lantern's twin, on the far side of the door. Lit, the two together steady the dusk-wind entirely — and the road beyond stops guttering my flame, as if it had been holding its breath.",
      gatedBy: { key: "watchfire_warmed", scope: "room" }, effect: { flame: 1, set: { lantern_right_lit: true } },
      polygon: [ { x: 170, y: 110 }, { x: 194, y: 110 }, { x: 194, y: 156 }, { x: 170, y: 156 } ] },
    // the Even Voice — the pull to stay, felt out where the warm light fails toward the open vale (grace)
    { id: "even_voice", verb: "examine", label: "the Even Voice", interactAt: { x: 330, y: 240 },
      line: "Out where the warm light fails and the cold vale opens, I can almost hear it — the Even Voice, gentle as a mother: stay, little ember; it is dark out there, and they need your warmth. It is not wrong. That is the hardest part.",
      polygon: [ { x: 296, y: 150 }, { x: 408, y: 150 }, { x: 408, y: 206 }, { x: 296, y: 206 } ] },
  ],
  // living-scene effects: the watch-fire blazing in the doorway throws flame + embers, the two
  // porch-lanterns glow, and a slow sunset sky drifts over the vale on the right. (Placed to the art.)
  fx: [
    { id: "watchfire_glow", kind: "glow", layer: "back", x: 98, y: 158, w: 66, h: 66, color: 0xffa53c, intensity: 1.05, seed: 1 },
    { id: "watchfire_flames", kind: "flames", layer: "back", x: 102, y: 148, w: 46, h: 72, intensity: 1.1, seed: 2 },
    { id: "watchfire_embers", kind: "embers", layer: "front", x: 120, y: 158, w: 30, h: 50, count: 12, intensity: 1, seed: 3 },
    { id: "lantern_l_glow", kind: "glow", layer: "back", x: 70, y: 110, w: 26, h: 30, color: 0xffc24a, intensity: 0.7, seed: 4 },
    { id: "lantern_r_glow", kind: "glow", layer: "back", x: 170, y: 112, w: 26, h: 30, color: 0xffc24a, intensity: 0.7, seed: 5 },
    { id: "vale_clouds", kind: "clouds", layer: "back", x: 240, y: 24, w: 240, h: 70, color: 0xffd0b0, count: 4, intensity: 1, seed: 6 },
    { id: "vale_birds", kind: "birds", layer: "back", x: 250, y: 50, w: 220, h: 60, color: 0x2a2533, count: 3, intensity: 1, seed: 7 },
  ],
  npcs: [
    { id: "morrock", x: 270, y: 242, radius: 9, color: 0x7a6e5c, label: "Old Morrock",
      dialog: [
        { text: "Where are you going with a flame that size, lad? The yard's edge is far enough. It always has been.", mood: "wary" },
        { text: "...Ah. You mean to take the road. I walked a little way down it once, long ago, and turned back — and I have been the lesser for it every day since. Go on, then. And do not you turn back.", mood: "sad" },
      ] },
  ],
  exits: [
    // back up the steps into the Even Hall (left). Always open — you can return home.
    { id: "back_to_hall", target: "even_hall", spawn: { x: 112, y: 250 }, transition: "fade",
      polygon: [ { x: 46, y: 236 }, { x: 92, y: 236 }, { x: 92, y: 264 }, { x: 46, y: 264 } ] },
    // the road east into the vale (right) — opens only once the watch-fire is warmed AND the flame
    // has grown bright enough (kindling the lanterns) to steady the cold wind. Pure flame-spine gate.
    { id: "to_dark_road", target: "dark_road", spawn: { x: 80, y: 220 }, transition: "fade",
      gatedBy: { key: "watchfire_warmed", scope: "room", minFlame: 4 },
      polygon: [ { x: 440, y: 213 }, { x: 478, y: 213 }, { x: 478, y: 264 }, { x: 440, y: 264 } ] },
  ],
};

// ---- Room 3: "The Dark Road" — Act II proper, the first true exterior (960×270). Out past the
// last wall, the road has gone to drift: three wandering way-stones must be settled in turn (the
// road steadies from where you stand, west→east), then the cold wayshrine rekindled, before the
// eastern climb will hold. Solitary — the only voices are the wind and the flame. Puzzle is
// KEY-CHAINED (robust to the growing carried flame); each act still grows the flame. ----
const darkRoad = {
  id: "dark_road",
  name: "The Dark Road",
  size: { w: 960, h: 270 },
  background: { color: 0x0f1319 }, // the Open: cold, near-lightless
  bgImage: "art/dark_road.png", // painted backdrop (ControlNet + pixelate)
  art:
    "Monkey-Island-1 hand-painted night exterior, cold and near-lightless, 960×270. The carried " +
    "flame is almost the only light — a small amber circle on packed earth. FAR LEFT: the last " +
    "turf-and-stone wall of the fold, a low gap still leaking faint hearth-glow, quickly swallowed by " +
    "dark. ALONG THE ROAD: three askew WAY-STONES half-sunk in drifting ground, the path beneath them " +
    "loose as cloth. CENTRE-EAST: a low cold WAYSHRINE — a stone bench and a rimed iron bowl long " +
    "unlit. RIGHT: a black FROST-GROVE of thorns and hoarfrost, and beyond it the eastern climb where " +
    "the very first faint band of dawn-grey shows low on the horizon. Palette: near-black blues and " +
    "slate, frost-white rime, the lone warm amber of the flame; deep shadow, a vast cold sky thick " +
    "with the sense of the Open. Mood: exposed, hushed, the courage of the first night on the road.",
  spawn: { x: 80, y: 220 },
  actorRadius: 9,
  interactRadius: 60,
  state: { stone_a_set: false, stone_b_set: false, stone_c_set: false, wayshrine_lit: false },
  walkmesh: [[ { x: 40, y: 165 }, { x: 920, y: 165 }, { x: 920, y: 250 }, { x: 40, y: 250 } ]],
  hotspots: [
    // a look back at the last wall of home — grace (the warmth behind is not for the road)
    { id: "wall_gap", verb: "examine", label: "the last wall of the fold", interactAt: { x: 110, y: 232 },
      line: "The last wall of the fold, and a gap in it leaking the smallest thread of hearth-light. If I look back, the warmth is still there, waiting to take me in again. The warmth behind is not for the road. I face east.",
      polygon: [ { x: 40, y: 150 }, { x: 120, y: 150 }, { x: 120, y: 185 }, { x: 40, y: 185 } ] },
    // three wandering way-stones, settled west->east (each grounds the road and grows the flame)
    { id: "way_stone_a", verb: "use", label: "a wandering way-stone", interactAt: { x: 360, y: 232 },
      line: "A way-stone, leaning and half-sunk, the road beneath it loose as cloth. By my flame I find which way it wants to sit, and set it true. It grinds home, and a length of the road goes firm beneath my feet.",
      effect: { flame: 1, set: { stone_a_set: true } },
      polygon: [ { x: 342, y: 226 }, { x: 382, y: 226 }, { x: 382, y: 246 }, { x: 342, y: 246 } ] },
    { id: "way_stone_b", verb: "use", label: "a wandering way-stone", interactAt: { x: 500, y: 232 },
      line: "The next stone, further out where the dark is deeper. The road only settles outward, a step at a time, from where one stands. I set it, and the firm ground reaches a little further into the night.",
      gatedBy: { key: "stone_a_set", scope: "room" }, effect: { flame: 1, set: { stone_b_set: true } },
      polygon: [ { x: 482, y: 226 }, { x: 522, y: 226 }, { x: 522, y: 246 }, { x: 482, y: 246 } ] },
    { id: "way_stone_c", verb: "use", label: "a wandering way-stone", interactAt: { x: 640, y: 232 },
      line: "The last of the three, where the road frays away entirely into the Open. I set it, and the whole road behind me lies steady and sure — a thread of certainty drawn out across the dark.",
      gatedBy: { key: "stone_b_set", scope: "room" }, effect: { flame: 1, set: { stone_c_set: true } },
      polygon: [ { x: 622, y: 226 }, { x: 662, y: 226 }, { x: 662, y: 246 }, { x: 622, y: 246 } ] },
    // the cold wayshrine — rekindle once the road is settled; the flame flares and the climb firms
    { id: "wayshrine", verb: "kindle", label: "the cold wayshrine", interactAt: { x: 780, y: 232 },
      line: "A wayshrine for travellers, its iron bowl dead under a crust of ice — no one has come this far to tend it in a long age. I rekindle it from my own flame, and it answers, leaping bright, and the eastern climb firms into solid stone before me.",
      gatedBy: { key: "stone_c_set", scope: "room" }, effect: { flame: 1, set: { wayshrine_lit: true } },
      polygon: [ { x: 758, y: 150 }, { x: 808, y: 150 }, { x: 808, y: 188 }, { x: 758, y: 188 } ] },
    // the frost-grove — grace: cup the flame beneath a branch and a single green bud shows
    { id: "frost_grove", verb: "kindle", label: "the frost-grove", interactAt: { x: 868, y: 232 },
      line: "A thicket of black thorn, every branch sheathed in hoarfrost. I cup my flame beneath the lowest bough; the ice runs, and for a moment a single green bud shows through. It will freeze again by morning. I warm it anyway.",
      polygon: [ { x: 838, y: 120 }, { x: 900, y: 120 }, { x: 900, y: 200 }, { x: 838, y: 200 } ] },
  ],
  decor: [
    { id: "sky", kind: "sky", z: 0, color: 0x0a0d12,
      shape: { type: "rect", x: 0, y: 0, w: 960, h: 165 },
      desc: "A vast near-black night sky over the Open, cold blue-black, pressing down on the small road." },
    { id: "dawn_band", kind: "horizon", z: 1, color: 0x2a2a38,
      shape: { type: "rect", x: 790, y: 150, w: 170, h: 15 },
      desc: "The very first faint band of dawn-grey, low on the eastern horizon beyond the climb — the promise the road is toward." },
    { id: "home_wall", kind: "wall", z: 2, color: 0x1a1a1f,
      shape: { type: "poly", points: [[0, 165], [128, 165], [128, 130], [60, 118], [0, 128]] },
      desc: "The last turf-and-stone wall of the fold at the road's mouth, dark and low, the edge of all that was kept." },
    { id: "home_gap_glow", kind: "window", z: 3, color: 0x5a3c1c, alpha: 0.5,
      shape: { type: "ellipse", cx: 70, cy: 150, rx: 50, ry: 26 },
      desc: "A faint amber leak of hearth-glow through a gap in the home-wall, already dimming behind the traveller." },
    { id: "stone_a", kind: "way_stone", z: 2, color: 0x2c2c33,
      shape: { type: "poly", points: [[342, 246], [350, 224], [378, 228], [382, 246]] },
      desc: "A leaning way-stone half-sunk and askew, frost in its lee, the path beneath it drifted loose." },
    { id: "stone_b", kind: "way_stone", z: 2, color: 0x2c2c33,
      shape: { type: "poly", points: [[482, 246], [492, 222], [518, 228], [522, 246]] },
      desc: "A second wandering stone, tilted the other way, rimed and cold." },
    { id: "stone_c", kind: "way_stone", z: 2, color: 0x2c2c33,
      shape: { type: "poly", points: [[622, 246], [628, 224], [658, 226], [662, 246]] },
      desc: "The third and farthest way-stone, nearly toppled, where the road frays into the dark." },
    { id: "wayshrine_bench", kind: "wayshrine", z: 2, color: 0x262a33,
      shape: { type: "rect", x: 756, y: 175, w: 54, h: 60 },
      desc: "A low weathered stone bench-shrine for travellers, moss-blacked and frost-furred, long unused." },
    { id: "wayshrine_bowl", kind: "brazier", z: 3, color: 0x3a3f4c,
      shape: { type: "ellipse", cx: 783, cy: 172, rx: 16, ry: 7 },
      desc: "A rimed iron offering-bowl atop the shrine, the traveller's fire dead in it under a crust of ice." },
    { id: "grove_trunk", kind: "tree", z: 2, color: 0x16161b, baselineY: 200,
      shape: { type: "rect", x: 846, y: 120, w: 46, h: 80 },
      desc: "The black trunk of a frost-grove thornthicket, branches knotted with hoarfrost." },
    { id: "grove_crown", kind: "root", z: 3, color: 0x101015,
      shape: { type: "poly", points: [[826, 122], [869, 70], [912, 122]] },
      desc: "A clawed crown of frozen thorn-branches over the grove, sharp against the night." },
  ],
  occluders: [
    { id: "grove_shadow", x: 846, y: 120, w: 46, h: 80, baselineY: 235, color: 0x0c0f14 },
  ],
  npcs: [],
  exits: [
    // back to the Doorstone porch (west)
    { id: "back", target: "doorstone", spawn: { x: 440, y: 235 }, transition: "fade",
      polygon: [ { x: 40, y: 170 }, { x: 62, y: 170 }, { x: 62, y: 250 }, { x: 40, y: 250 } ] },
    // the eastern climb to the Hollow — holds only once the wayshrine is lit (the road settled)
    { id: "to_hollow", target: "hollow", spawn: { x: 80, y: 235 }, transition: "fade",
      gatedBy: { key: "wayshrine_lit", scope: "room" },
      polygon: [ { x: 896, y: 170 }, { x: 920, y: 170 }, { x: 920, y: 250 }, { x: 896, y: 250 } ] },
  ],
};

// ---- Room 4: "The Hollow" — Act II, the second trial (720×270). A frost-choked sunken hollow
// where the Grey pools. Warm the frozen spring, then the thorn that remembers green (the flame
// grows) to firm the eastern climb. At the hollow's heart the Gutterling pleads for light — the
// cold guest the even-mercy would take in; the brighter flame drives it back. Mercy exploited →
// the seeing that saves. Puzzle KEY-chained (spring → thorn → climb). ----
const hollow = {
  id: "hollow",
  name: "The Hollow",
  size: { w: 720, h: 270 },
  background: { color: 0x1a171c }, // violet-dark, frost-choked
  bgImage: "art/hollow.png", // painted backdrop (ControlNet + pixelate)
  art:
    "A frost-choked hollow under low violet dusk, ~720×270. Black thorn-brakes rise like broken walls " +
    "left and right, iced branches catching stray motes of the hero's flame in brittle silver. " +
    "Centre-left a shallow stone bowl cradles the FROZEN SPRING — milk-white ice over midnight-blue " +
    "water, rim feathered with hoarfrost. Right of it a low THORN-BUSH crouches black as iron, one " +
    "ember-green leaf just visible beneath the rime. At the hollow's heart the GUTTERLING — a hunched " +
    "charcoal-grey wisp the size of a curled child — kneels on the frost, palms open toward the flame, " +
    "pleading. Ground is iron-hard frost veined with pale cracks; the air breath-thick mist; the sky " +
    "low and violet, lit only by the one carried flame. Every surface glints cold indigo and slate, " +
    "except where the flame's orange lick finds a living colour beneath the ice. Mood: pity, danger, " +
    "and the cold truth that the Grey only takes.",
  spawn: { x: 80, y: 235 },
  actorRadius: 9,
  interactRadius: 60,
  state: { spring_warmed: false, thorn_warmed: false },
  walkmesh: [[ { x: 60, y: 210 }, { x: 660, y: 210 }, { x: 660, y: 250 }, { x: 60, y: 250 } ]],
  hotspots: [
    // warm the frozen spring — a living thing under the ice (flame grows)
    { id: "frozen_spring", verb: "kindle", label: "the frozen spring", interactAt: { x: 160, y: 235 },
      line: "A spring locked under milk-white ice — but bring the flame close and there is blue water moving beneath, and something turquoise and alive. I warm it until the ice gives, and the little spring breathes again. The flame stands taller for the kindness.",
      effect: { flame: 1, set: { spring_warmed: true } },
      polygon: [ { x: 138, y: 184 }, { x: 184, y: 184 }, { x: 184, y: 210 }, { x: 138, y: 210 } ] },
    // the thorn that remembers green — warm it once the spring is open; firms the eastern climb
    { id: "thorn_bush", verb: "kindle", label: "the thorn that remembers green", interactAt: { x: 300, y: 235 },
      line: "A thorn-bush black as iron, and yet one leaf beneath the rime still holds a memory of green. With the spring running again I warm the thorn too, and the leaf uncurls. Two living things, woken in a dead place — and the climb east firms under the brighter light.",
      gatedBy: { key: "spring_warmed", scope: "room" }, effect: { flame: 1, set: { thorn_warmed: true } },
      polygon: [ { x: 278, y: 174 }, { x: 324, y: 174 }, { x: 324, y: 210 }, { x: 278, y: 210 } ] },
  ],
  decor: [
    { id: "sky", kind: "sky", z: 0, color: 0x2b2832,
      shape: { type: "rect", x: 0, y: 0, w: 720, h: 160 },
      desc: "A low violet dusk sky pressing on the hollow, cold and starless but for the carried flame." },
    { id: "frost_mist", kind: "sky", z: 1, color: 0x3a3741, alpha: 0.7,
      shape: { type: "rect", x: 0, y: 150, w: 720, h: 64 },
      desc: "Breath-thick frost-mist hanging across the hollow, blue-grey, swallowing the thorn-walls' feet." },
    { id: "thorn_wall_left", kind: "tree", z: 2, color: 0x262329,
      shape: { type: "poly", points: [[0, 160], [78, 160], [60, 210], [0, 210]] },
      desc: "A black thorn-brake rising like a broken wall at the west, iced branches glassy as daggers." },
    { id: "thorn_wall_right", kind: "tree", z: 2, color: 0x262329,
      shape: { type: "poly", points: [[642, 160], [720, 160], [720, 210], [660, 210]] },
      desc: "The eastern thorn-brake, black and iron-iced, parting just enough for the climb beyond." },
    { id: "spring_rim", kind: "stone", z: 2, color: 0x505866,
      shape: { type: "ellipse", cx: 161, cy: 198, rx: 26, ry: 12 },
      desc: "A shallow stone bowl ringed with hoarfrost, cradling the spring." },
    { id: "spring_ice", kind: "water", z: 3, color: 0x3c4650,
      shape: { type: "ellipse", cx: 161, cy: 196, rx: 19, ry: 8 },
      desc: "Milk-white ice glazed over midnight-blue water, a turquoise gleam of life just visible beneath." },
    { id: "thorn_bush_shape", kind: "tree", z: 3, color: 0x222026,
      shape: { type: "poly", points: [[280, 208], [288, 176], [304, 168], [320, 178], [322, 208]] },
      desc: "A low thorn-bush black as iron, one ember-green leaf surviving under its rime." },
    { id: "creeping_grey", kind: "danger", z: 2, color: 0x36333b, alpha: 0.8,
      shape: { type: "poly", points: [[418, 196], [524, 184], [544, 214], [430, 222]] },
      desc: "A bruise of creeping Grey pooled at the hollow's heart, colourless and cold, dimming the frost around the Gutterling." },
    { id: "gutterling_form", kind: "root", z: 3, color: 0x4a4653, baselineY: 222,
      shape: { type: "ellipse", cx: 482, cy: 210, rx: 14, ry: 14 },
      desc: "The Gutterling: a hunched charcoal-grey wisp the size of a curled child, palms cupped open toward the flame, pleading." },
    { id: "ground_frost", kind: "floor_inlay", z: 0, color: 0x232026,
      shape: { type: "rect", x: 0, y: 210, w: 720, h: 60 },
      desc: "Iron-hard frosted ground veined with pale cracks, glinting cold underfoot." },
  ],
  occluders: [
    { id: "thornwall_l", x: 8, y: 150, w: 40, h: 60, baselineY: 210, color: 0x1d1b21 },
    { id: "thornwall_r", x: 672, y: 150, w: 40, h: 60, baselineY: 210, color: 0x1d1b21 },
  ],
  npcs: [
    { id: "gutterling", x: 482, y: 224, radius: 10, color: 0x4a4653, label: "the Gutterling",
      dialog: [
        { text: "...cold. So cold. Give me a little of your light — only a little. You have so much, and I have none at all.", mood: "sad" },
        { text: "Just a touch. The Wardmothers never refused me. They never refuse anyone... that is how I have grown so fat on so many small mercies.", mood: "wary" },
      ] },
  ],
  exits: [
    // back down the western descent to the Dark Road
    { id: "back", target: "dark_road", spawn: { x: 880, y: 220 }, transition: "fade",
      polygon: [ { x: 60, y: 210 }, { x: 84, y: 210 }, { x: 84, y: 250 }, { x: 60, y: 250 } ] },
    // forward (the eastern climb) is wired to the Wind (room 5) when that room is built;
    // it firms only once the thorn (and so the spring) is warmed.
  ],
};

export const rooms: Record<string, Room> = {
  [evenHall.id]: loadRoom(evenHall),
  [doorstone.id]: loadRoom(doorstone),
  [darkRoad.id]: loadRoom(darkRoad),
  [hollow.id]: loadRoom(hollow),
};
export const startRoomId = evenHall.id;
// MI1 framing: the view shows ~270 room-units tall; the renderer fits this HEIGHT to the window
// so it fills the screen and characters read large. Width is the headless fallback only.
export const view = { w: 480, h: 270 };
export const scale = 4;
export const quest: QuestSpec = {
  win: [{ key: "hall_opened", scope: "global" }], // kindle the brazier, open the woven gate, leave
  initialGlobal: { flame: 1 }, // Brand sets out carrying a small, even flame
};
